/**
 * IconPicker — reusable popover matrix for picking a classroom icon (PRD-30
 * §FR-8). A trigger button shows the current icon + chevron; opening reveals a
 * search input, a 6-column grid of the allowlisted icons (tinted to the
 * subject accent), and an optional reset row.
 *
 * Accessibility (PRD-30 NFR, accessibility.md): keyboard reachable (Tab into
 * grid), Enter/Space selects, Esc closes and returns focus to the trigger,
 * outside pointer-down closes.
 *
 * Positioning, dismissal and Tab containment come from `useAnchoredPanel` —
 * the same primitive `<Dropdown>` uses, so the picker escapes a scrollable
 * `<Modal>` body without being clipped and still bounds its own height. The
 * panel is `role="dialog"`, so it traps Tab (`trapFocus`): the modal's own
 * focus trap only walks its subtree and cannot see a portalled panel.
 *
 * All user-facing strings are passed in as props (searchPlaceholder, resetLabel,
 * label) so the i18next/no-literal-string lint passes — the integrating feature
 * supplies translated text. The reset row only renders when `resetLabel` is set.
 */

import { useAnchoredPanel } from "@hooks/useAnchoredPanel"
import { cn } from "@lib/cn"
import { ChevronDown, RotateCcw, Search } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import {
  CLASSROOM_ICON_NAMES,
  accentForSubject,
  defaultIconForSubject,
  iconComponentFor,
} from "@/constants/classroomIcons"

/** Panel width in px — matches the `w-72` this panel used to carry. */
const PANEL_WIDTH = 288
/**
 * Room for the search row + the `max-h-56` grid + the reset row. Taller than
 * the hook's default so a roomy viewport shows the whole matrix; on a short
 * one the hook clamps below this and the panel scrolls.
 */
const PANEL_MAX_HEIGHT = 360

interface Props {
  /** Currently-selected icon NAME (e.g. "FlaskConical"). */
  value: string
  /** Subject value — drives the reset target + icon tint. */
  subject: string
  onChange: (name: string) => void
  /** Optional explicit reset handler; defaults to selecting the subject default. */
  onReset?: () => void
  disabled?: boolean
  /** aria-label for the trigger button. */
  label?: string
  /** Placeholder for the search input (translated by the caller). */
  searchPlaceholder?: string
  /** Label for the reset row (translated by the caller). When unset, the row is hidden. */
  resetLabel?: string
}

export function IconPicker({
  value,
  subject,
  onChange,
  onReset,
  disabled,
  label,
  searchPlaceholder = "",
  resetLabel = "",
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  // Roving-tabindex anchor: index (into `filtered`) of the single grid cell that
  // is in the Tab order. Arrow keys move it; everything else is tabIndex -1, so
  // Tab enters/leaves the 37-cell matrix as one stop (WAI-ARIA grid pattern).
  const [activeIndex, setActiveIndex] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const { triggerRef, panelRef, panelStyle, close } = useAnchoredPanel({
    open,
    onClose: () => setOpen(false),
    width: PANEL_WIDTH,
    maxHeight: PANEL_MAX_HEIGHT,
    trapFocus: true,
  })

  // Number of columns in the matrix — keeps the arrow-key math (Up/Down jump a
  // whole row) in sync with the `grid-cols-6` class below.
  const GRID_COLS = 6

  const accent = accentForSubject(subject)
  const TriggerIcon = iconComponentFor(value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return CLASSROOM_ICON_NAMES
    return CLASSROOM_ICON_NAMES.filter((name) => name.toLowerCase().includes(q))
  }, [query])

  // Reset the search field whenever the popover closes.
  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  // On open, anchor the roving focus on the currently-selected icon (or the
  // first cell) so Tab lands somewhere meaningful.
  useEffect(() => {
    if (open) {
      const i = CLASSROOM_ICON_NAMES.indexOf(value as (typeof CLASSROOM_ICON_NAMES)[number])
      setActiveIndex(i >= 0 ? i : 0)
    }
  }, [open, value])

  // Filtering reflows the grid — move the anchor back to the first match.
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Focus the search field on open — the picker is a dialog, so focus belongs
  // inside it. `trapFocus` on the hook keeps Tab from escaping to the browser
  // chrome past the portal.
  useEffect(() => {
    if (!open) return
    const handle = window.requestAnimationFrame(() => searchRef.current?.focus())
    return () => window.cancelAnimationFrame(handle)
  }, [open])

  const select = (name: string) => {
    onChange(name)
    close()
  }

  const handleReset = () => {
    if (onReset) onReset()
    else onChange(defaultIconForSubject(subject))
    close()
  }

  // Arrow-key roving focus across the icon matrix: ←/→ step one cell, ↑/↓ jump a
  // full row (GRID_COLS), Home/End go to the first/last cell. Tab still works
  // (every cell is a real <button>); this just adds the spreadsheet-style nav a
  // 37-cell grid wants. Reads the live DOM order so it tracks the search filter.
  const onGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const NAV = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"]
    if (!NAV.includes(e.key)) return
    const cells = Array.from(
      gridRef.current?.querySelectorAll<HTMLButtonElement>("button[data-icon-cell]") ?? [],
    )
    if (cells.length === 0) return
    const current = cells.findIndex((c) => c === document.activeElement)
    let next = current
    switch (e.key) {
      case "ArrowRight":
        next = current + 1
        break
      case "ArrowLeft":
        next = current - 1
        break
      case "ArrowDown":
        next = current + GRID_COLS
        break
      case "ArrowUp":
        next = current - GRID_COLS
        break
      case "Home":
        next = 0
        break
      case "End":
        next = cells.length - 1
        break
    }
    if (current === -1) next = 0 // focus was outside the grid → enter at the top
    if (next < 0 || next >= cells.length) return // edge: don't wrap
    e.preventDefault()
    setActiveIndex(next)
    cells[next]?.focus()
  }

  return (
    <div className="relative w-fit text-left">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "rounded-control border-border bg-surface text-ink hover:bg-inset inline-flex min-h-11 items-center gap-1.5 border px-2.5 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-10",
        )}
      >
        <TriggerIcon className={cn("h-5 w-5", accent)} aria-hidden />
        <ChevronDown
          className={cn(
            "text-ink-subtle h-4 w-4 transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={label}
            style={{ ...panelStyle, width: PANEL_WIDTH }}
            className="rounded-card border-border bg-surface shadow-float z-50 overflow-y-auto overscroll-contain border focus:outline-none"
          >
            {/* Search */}
            <div className="border-hairline border-b p-2">
              <div className="relative">
                <Search
                  className="text-ink-subtle pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2"
                  aria-hidden
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder || label}
                  className="rounded-control border-border bg-surface text-ink placeholder:text-ink-subtle focus:border-accent min-h-11 w-full border pr-2.5 pl-8 text-sm md:min-h-9"
                />
              </div>
            </div>

            {/* Matrix. onKeyDown on the container only manages roving focus
              between the real <button> cells below (arrow/Home/End) — the
              interactive elements are the buttons, not this div. */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              ref={gridRef}
              onKeyDown={onGridKeyDown}
              className="grid max-h-56 grid-cols-6 gap-1 overflow-y-auto p-2"
            >
              {filtered.map((name, index) => {
                const Icon = iconComponentFor(name)
                const selected = name === value
                return (
                  <button
                    key={name}
                    type="button"
                    data-icon-cell
                    title={name}
                    aria-label={name}
                    aria-pressed={selected}
                    tabIndex={index === activeIndex ? 0 : -1}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => select(name)}
                    className={cn(
                      "rounded-control flex h-11 w-11 items-center justify-center md:h-10 md:w-10",
                      selected ? "bg-accent-soft ring-accent ring-2" : "hover:bg-inset",
                    )}
                  >
                    <Icon className={cn("h-5 w-5", accent)} aria-hidden />
                  </button>
                )
              })}
            </div>

            {/* Reset row — only when a translated label is supplied. */}
            {resetLabel && (
              <div className="border-hairline border-t p-1">
                <button
                  type="button"
                  onClick={handleReset}
                  className="rounded-control text-ink-muted hover:bg-inset flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm md:min-h-9"
                >
                  <RotateCcw className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{resetLabel}</span>
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
